import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, FileText, MessageSquare, CheckCircle2, ChevronDown, Calendar, GraduationCap, Briefcase, Trophy } from "lucide-react";

const STEPS = [
  {
    n: "01", icon: FileText,
    title: "Candidature en ligne",
    desc: "CV + lettre de motivation en -5 minutes.",
    detail: "Remplissez le formulaire en ligne, joignez votre CV et votre lettre de motivation. Notre équipe accuse réception sous 24h.",
  },
  {
    n: "02", icon: MessageSquare,
    title: "Entretien personnalisé",
    desc: "Avec un membre de notre équipe pédagogique.",
    detail: "Un entretien de 20 à 30 minutes pour mieux vous connaître, évaluer votre projet et répondre à toutes vos questions.",
  },
  {
    n: "03", icon: CheckCircle2,
    title: "Réponse sous 48h",
    desc: "Une décision claire et rapide, pas d'attente interminable.",
    detail: "Vous recevez une réponse définitive sous 48 heures. Si accepté, votre place est immédiatement réservée pour la rentrée choisie.",
  },
];

const RENTREES = [
  { mois: "Janvier", dispo: true },
  { mois: "Avril", dispo: true },
  { mois: "Juillet", dispo: true },
  { mois: "Octobre", dispo: true },
];

const PROFILS = [
  { icon: GraduationCap, label: "Bachelor BBA", level: "Post-bac → Bac+3", color: "#002362", desc: "Bac validé (général, technologique ou professionnel), ou classes préparatoires, BTS, BUT, Licence 1 ou 2." },
  { icon: Briefcase, label: "Master MBA", level: "Bac+3 → Bac+5", color: "#ED2939", desc: "Licence ou Bachelor validé. Admission parallèle possible en Bac+4." },
  { icon: Trophy, label: "MBA Executive", level: "Bac+5+", color: "#C0392B", desc: "Bac+5 validé et minimum 5 ans d'expérience professionnelle. Sélection sur dossier et entretien." },
];

const FAQ = [
  {
    q: "Quels sont les prérequis pour candidater ?",
    a: "À chaque profil sa voie :\n• Bachelor — Bac validé\n• Master — Bac+3, (les filières spécialités disponibles)\n• Executive MBA — Bac+5 et au moins 5 ans d'expérience\nNotre conviction : votre motivation et votre projet professionnel comptent tout autant que votre dossier académique.",
  },
  {
    q: "Quel est le coût des formations ?",
    a: "Des frais de scolarité adaptés à chaque profil, pour vous accompagner : contactez-nous pour établir votre devis personnalisé.",
  },
  {
    q: "Le diplôme est-il celui de l'ECL France ?",
    a: "Oui, rien ne change sauf le lieu. Mêmes programmes, mêmes diplômes, même reconnaissance internationale.",
  },
  {
    q: "Les cours sont-ils en français ou en anglais ?",
    a: "Les formations est majoritairement en français, mais intègre des modules en anglais pour développer votre agilité linguistique et votre compétitivité sur le marché global.",
  },
  {
    q: "Quels sont les délais de candidature ?",
    a: "Les candidatures sont ouvertes en continu, mais nous recommandons de postuler 1 mois avant la rentrée.",
  },
];

const FaqItem = ({ f, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.07 }}
      className="border-b border-navy-100 last:border-0"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 py-5 text-left group"
        data-testid={`faq-trigger-${index}`}
      >
        <span className={`font-display text-lg md:text-xl transition-colors ${open ? "text-red-500" : "text-navy-700 group-hover:text-navy-500"}`}>
          {f.q}
        </span>
        <ChevronDown className={`h-5 w-5 shrink-0 mt-1 transition-all duration-300 ${open ? "rotate-180 text-red-500" : "text-navy-400"}`} />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.3 }}
          className="pb-5"
        >
          {f.a.split("\n").map((line, j) => (
            <span key={j} className="block text-[15px] text-[#555] leading-relaxed">{line}</span>
          ))}
        </motion.div>
      )}
    </motion.div>
  );
};

export const Admissions = () => {
  const navigate = useNavigate();

  return (
    <div id="admissions" data-testid="admissions-section">

      {/* Steps */}
      <section className="section-padding bg-white">
        <div className="container-edge">
          <div className="max-w-3xl mb-14">
            <div className="overline mb-4">Admission</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight">
              Demain ne se construit pas <em className="text-navy-500">tout seul</em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.15 }}
                  className="relative bg-[#FAF8F3] border border-navy-100 p-8 md:p-10 overflow-hidden group hover:shadow-lg transition-shadow"
                  data-testid={`admission-step-${s.n}`}
                >
                  {/* Step number */}
                  <span className="absolute -top-3 right-5 font-display text-[110px] leading-none text-navy-700/[0.06] group-hover:text-navy-700/[0.09] transition-colors select-none" aria-hidden>
                    {s.n}
                  </span>
                  {/* Top accent */}
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-navy-500 to-transparent" />

                  <div className="relative">
                    <div className="w-12 h-12 bg-navy-500 group-hover:bg-red-500 transition-colors duration-500 flex items-center justify-center mb-6">
                      <Icon className="h-5 w-5 text-white" strokeWidth={1.6} />
                    </div>
                    <h3 className="font-display text-2xl text-navy-700 mb-2 group-hover:text-navy-500 transition-colors">{s.title}</h3>
                    <p className="text-red-500 text-[12px] uppercase tracking-[0.16em] font-semibold mb-3">{s.desc}</p>
                    <p className="text-[#555] text-[14px] leading-relaxed">{s.detail}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Profils & Rentrées */}
      <section className="section-padding bg-[#FAF8F3]">
        <div className="container-edge">
          <div className="grid grid-cols-12 gap-10 lg:gap-16">

            {/* Profils */}
            <div className="col-span-12 lg:col-span-7">
              <div className="overline mb-4">Votre profil</div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-navy-700 mb-8">
                À chaque profil <em className="not-italic red-shine">sa voie</em>
              </h2>
              <div className="space-y-4">
                {PROFILS.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <motion.div
                      key={p.label}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className="bg-white border border-navy-100 p-6 flex gap-5 items-start group hover:shadow-md transition-shadow"
                    >
                      <div className="w-12 h-12 flex items-center justify-center shrink-0 transition-colors duration-500" style={{ background: p.color }}>
                        <Icon className="h-5 w-5 text-white" strokeWidth={1.6} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-baseline gap-3 mb-1">
                          <h3 className="font-display text-xl text-navy-700">{p.label}</h3>
                          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white px-2 py-0.5" style={{ background: p.color }}>{p.level}</span>
                        </div>
                        <p className="text-[#555] text-[14px] leading-relaxed">{p.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Rentrées + CTA */}
            <div className="col-span-12 lg:col-span-5 space-y-6">
              {/* Rentrées */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white border border-navy-100 p-8"
              >
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="h-5 w-5 text-navy-500" strokeWidth={1.5} />
                  <div className="overline">4 rentrées par an</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {RENTREES.map((r) => (
                    <div key={r.mois} className="border border-navy-100 px-4 py-3 text-center group hover:border-red-500 hover:bg-red-50 transition-all cursor-default">
                      <div className="font-display text-xl text-navy-700 group-hover:text-red-500 transition-colors">{r.mois}</div>
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400 mx-auto mt-1" />
                    </div>
                  ))}
                </div>
                <p className="text-[12px] text-[#888] mt-4 text-center">Candidatures ouvertes en continu</p>
              </motion.div>

              {/* CTA block */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="relative bg-navy-700 p-8 overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-[3px] flex">
                  <div className="flex-1 bg-navy-400" />
                  <div className="flex-1 bg-white/30" />
                  <div className="flex-1 bg-red-500" />
                </div>
                <svg className="absolute inset-0 w-full h-full opacity-[0.06] pointer-events-none" preserveAspectRatio="none">
                  <defs><pattern id="adm-dots" width="16" height="16" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1" fill="white" /></pattern></defs>
                  <rect width="100%" height="100%" fill="url(#adm-dots)" />
                </svg>
                <div className="relative">
                  <div className="text-white/60 text-[10px] uppercase tracking-[0.22em] mb-2">Prêt(e) ?</div>
                  <h3 className="font-display text-2xl text-white mb-3">L'avenir ne s'attend pas.</h3>
                  <p className="text-white/70 text-[14px] leading-relaxed mb-6">
                    Envoyez votre candidature dès aujourd'hui. Notre équipe vous répond sous 48 heures.
                  </p>
                  <button
                    onClick={() => navigate("/contact#candidater")}
                    className="inline-flex items-center gap-2 bg-white text-navy-700 px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-white/90 transition-colors w-full justify-center"
                    data-testid="admissions-cta-apply"
                  >
                    Candidater maintenant
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Admissions;
